import React, { useState, useRef, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import * as api from '../services/apiService';
import XteViewer from './XteViewer';
import XteGenerator from './XteGenerator';
import AdminPanel from './AdminPanel';
import ModernHeader from './ModernHeader';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

const TissAnalyzer = () => {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('visualizar');
  const [xteFiles, setXteFiles] = useState([]);
  const [xtrFiles, setXtrFiles] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState({
    validating: false,
    analyzing: false,
    downloadingAnalysis: false,
    downloadingXtr: false,
  });
  const [xtrOnlyFiles, setXtrOnlyFiles] = useState([]);
  const [results, setResults] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const xteFileRef = useRef(null);
  const xtrFileRef = useRef(null);

  useEffect(() => {
  const fetchUserData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);

    if (user) {
      const { data, error } = await supabase
        .from('profiles')
        .select(`*, companies (razao_social, nome_fantasia)`)
        .eq('id', user.id)
        .single();

      if (error) {
        console.error("Erro ao buscar perfil do usuário:", error);
      } else {
        setProfileData(data);
        const admin = data.role === 'admin';
        setIsAdmin(admin);

        if (admin) {
          setActiveTab('admin');
        }
      }
    }
  };
  fetchUserData();
}, []);


  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setXteFiles([]);
    setXtrFiles([]);
    setResults(null);
    setValidationResults(null);
    setError('');
    setActiveTab('visualizar');
  };

  const getAuthToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      handleLogout();
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    return session.access_token;
  };

  const resetState = () => {
    setError('');
    setSuccessMessage('');
    setResults(null);
    setValidationResults(null);
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const handleFileChange = (setter) => (e) => {
    setter(Array.from(e.target.files));
    resetState();
  };

  const clearFiles = () => {
    setXteFiles([]);
    setXtrFiles([]);
    if (xteFileRef.current) xteFileRef.current.value = '';
    if (xtrFileRef.current) xtrFileRef.current.value = '';
    resetState();
  };

  //Função para validar se os nomes dos arquivos são iguais (sem extensão)
  const validateFileNames = () => {
    //Extrai nomes sem extensão
    const xteNames = xteFiles.map(file => file.name.replace(/\.[^/.]+$/, '').toLowerCase());
    const xtrNames = xtrFiles.map(file => file.name.replace(/\.[^/.]+$/, '').toLowerCase());
    
    //Verifica se há pelo menos um arquivo com nome correspondente
    const hasMatch = xteNames.some(xteName => xtrNames.includes(xteName));
    
    if (!hasMatch) {
      //Lista os nomes para o usuário
      const xteList = xteFiles.map(f => f.name).join(', ');
      const xtrList = xtrFiles.map(f => f.name).join(', ');
      
      setError(
        `Os nomes dos arquivos XTE e XTR devem ser IDÊNTICOS (sem considerar a extensão).\n\n` +
        `Arquivos XTE: ${xteList}\n` +
        `Arquivos XTR: ${xtrList}\n\n` +
        `Exemplo correto: "LOTE_123.xte" e "LOTE_123.xtr"`
      );
      return false;
    }
    
    const unmatchedXte = xteNames.filter(xteName => !xtrNames.includes(xteName));
    const unmatchedXtr = xtrNames.filter(xtrName => !xteNames.includes(xtrName));
    
    if (unmatchedXte.length > 0 || unmatchedXtr.length > 0) {
      let errorMsg = 'Alguns arquivos não possuem correspondência:\n\n';
      
      if (unmatchedXte.length > 0) {
        const unmatchedXteFiles = xteFiles
          .filter(f => unmatchedXte.includes(f.name.replace(/\.[^/.]+$/, '').toLowerCase()))
          .map(f => f.name)
          .join(', ');
        errorMsg += `⚠️ XTE sem XTR correspondente: ${unmatchedXteFiles}\n`;
      }
      
      if (unmatchedXtr.length > 0) {
        const unmatchedXtrFiles = xtrFiles
          .filter(f => unmatchedXtr.includes(f.name.replace(/\.[^/.]+$/, '').toLowerCase()))
          .map(f => f.name)
          .join(', ');
        errorMsg += `⚠️ XTR sem XTE correspondente: ${unmatchedXtrFiles}\n`;
      }
      
      errorMsg += '\nCada arquivo XTE deve ter um XTR com o mesmo nome (e vice-versa).';
      
      setError(errorMsg);
      return false;
    }
    
    return true;
  };

  const handleAnalyze = async () => {
    resetState();
    setLoading(prev => ({ ...prev, analyzing: true }));
    try {
      if (!xteFiles.length || !xtrFiles.length) {
        setError('Selecione pelo menos um arquivo XTE e um arquivo XTR');
        setLoading(prev => ({ ...prev, analyzing: false }));
        return;
      }

      //PRIMEIRA VERIFICAÇÃO: Valida se os nomes são iguais
      if (!validateFileNames()) {
        setLoading(prev => ({ ...prev, analyzing: false }));
        return;
      }

      const token = await getAuthToken();
      const data = await api.analyzeFilesAPI(xteFiles, xtrFiles, token);
      setResults(data);
      showSuccess('Análise completa realizada com sucesso!');
    } catch (error) {
      const errorMessage = error.response?.data?.detail?.mensagem || error.message;
      setError(errorMessage);
    } finally {
      setLoading(prev => ({ ...prev, analyzing: false }));
    }
  };

  const downloadFile = (blob, defaultFilename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = defaultFilename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  };

  const handleDownloadAnalysis = async () => {
    // REMOVA OU COMENTE ESTA LINHA:
    // resetState(); 
    
    // ADICIONE ESTAS DUAS LINHAS (apenas limpa mensagens, mantém os dados):
    setError('');
    setSuccessMessage('');

    setLoading(prev => ({ ...prev, downloadingAnalysis: true }));
    try {
      const token = await getAuthToken();
      const blob = await api.downloadAnalysisExcelAPI(xteFiles, xtrFiles, token);
      downloadFile(blob, `analise_completa_tiss_${Date.now()}.xlsx`);
      showSuccess('Relatório de análise baixado com sucesso!');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(prev => ({ ...prev, downloadingAnalysis: false }));
    }
  };

  const handleDownloadXtrReport = async (filesSource = 'analysis') => {
    resetState();
    setLoading(prev => ({ ...prev, downloadingXtr: true }));
    try {
      const token = await getAuthToken();
      //Usa xtrOnlyFiles se for da aba exclusiva, senão usa xtrFiles da análise
      const filesToSend = filesSource === 'standalone' ? xtrOnlyFiles : xtrFiles;
      
      if (!filesToSend || filesToSend.length === 0) {
        setError('Por favor, selecione pelo menos um arquivo XTR.');
        return;
      }
      
      const blob = await api.downloadXtrReportAPI(filesToSend, token);
      downloadFile(blob, `relatorio_xtr_formatado_${Date.now()}.xlsx`);
      showSuccess('Relatório XTR baixado com sucesso!');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(prev => ({ ...prev, downloadingXtr: false }));
    }
  };

  const tabThemes = {
    'visualizar': 'theme-blue',
    'analisar': 'theme-green',
    'relatorio-xtr': 'theme-amber',
    'gerar': 'theme-purple',
    'admin': 'theme-blue'
  };

  const tabs = [
    ...(isAdmin ? [{ id: 'admin', name: 'Administração', icon: '🛡️', component: 'AdminPanel', theme: 'blue' }] : []),
    ...(!isAdmin ? [
      { id: 'visualizar', name: 'Conversor XTE/Excel', icon: '📊', component: 'XteViewer', theme: 'blue' },
      { id: 'analisar', name: 'Analisar Retorno', icon: '🔍', component: 'AnalysisTab', theme: 'green' },
      { id: 'relatorio-xtr', name: 'Relatório XTR', icon: '📄', component: 'XtrReportTab', theme: 'amber' },
      { id: 'gerar', name: 'Converta sua Planilha Excel em Arquivo XTE', icon: '✍️', component: 'XteGenerator', theme: 'purple' }
    ] : [])
  ];

  const currentTheme = tabThemes[activeTab] || 'theme-blue';

  const anyFileSelected = xteFiles.length > 0 || xtrFiles.length > 0;
  const fullAnalysisReady = xteFiles.length > 0 && xtrFiles.length > 0;
  const xtrReportReady = xtrFiles.length > 0;
  const isAnyLoading = Object.values(loading).some(Boolean);

  // Função para retornar classes de cor baseadas no tema da aba
  const getTabActiveClass = (theme) => {
    const themeClasses = {
      'blue': 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md',
      'green': 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-md',
      'amber': 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md',
      'purple': 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-md'
    };
    return themeClasses[theme] || themeClasses['blue'];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <ModernHeader
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        user={user}
        profileData={profileData}
        isAdmin={isAdmin}
        handleLogout={handleLogout}
      />

      {/* Main Content com tema dinâmico */}
      <main className={`container mx-auto px-4 lg:px-8 py-8 ${currentTheme}`}>
        {(error || successMessage) && (
          <div className="fixed top-24 right-4 z-50 max-w-md animate-slide-in-right">
            {error && (
              <div className="alert-error mb-4 shadow-xl">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-semibold">Erro</p>
                    <p className="text-sm">{error}</p>
                  </div>
                  <button onClick={() => setError('')} className="ml-auto">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            {successMessage && (
              <div className="alert-success shadow-xl">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-semibold">Sucesso</p>
                    <p className="text-sm">{successMessage}</p>
                  </div>
                  <button onClick={() => setSuccessMessage('')} className="ml-auto">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="animate-fade-in">
          {activeTab === 'admin' && (
            <AdminPanel session={{ user }} supabase={supabase} />
          )}

          {activeTab === 'visualizar' && (
            <XteViewer session={{ user }} supabase={supabase} />
          )}

          {activeTab === 'analisar' && (
            <AnalysisTab
              xteFiles={xteFiles}
              setXteFiles={setXteFiles}
              xtrFiles={xtrFiles}
              setXtrFiles={setXtrFiles}
              xteFileRef={xteFileRef}
              xtrFileRef={xtrFileRef}
              loading={loading}
              results={results}
              anyFileSelected={anyFileSelected}
              fullAnalysisReady={fullAnalysisReady}
              xtrReportReady={xtrReportReady}
              isAnyLoading={isAnyLoading}
              handleFileChange={handleFileChange}
              handleAnalyze={handleAnalyze}
              handleDownloadAnalysis={handleDownloadAnalysis}
              handleDownloadXtrReport={handleDownloadXtrReport}
              clearFiles={clearFiles}
            />
          )}

          {activeTab === 'relatorio-xtr' && (
            <XtrReportTab
              xtrFiles={xtrOnlyFiles}
              setXtrFiles={setXtrOnlyFiles}
              loading={loading.downloadingXtr}
              handleDownloadXtrReport={handleDownloadXtrReport}
              handleFileChange={handleFileChange}
            />
          )}

          {activeTab === 'gerar' && (
            <XteGenerator session={{ user }} supabase={supabase} />
          )}
        </div>
      </main>
    </div>
  );
};

// Componente AnalysisTab Atualizado (Troca apenas a área de ação)
const AnalysisTab = ({
  xteFiles, setXteFiles, xtrFiles, setXtrFiles, xteFileRef, xtrFileRef,
  loading, results, anyFileSelected, fullAnalysisReady,
  xtrReportReady, isAnyLoading, handleFileChange, handleAnalyze,
  handleDownloadAnalysis, handleDownloadXtrReport, clearFiles
}) => {
  const [dragActive, setDragActive] = useState({ xte: false, xtr: false });

  const handleDrag = (type, active) => {
    setDragActive(prev => ({ ...prev, [type]: active }));
  };

  const handleDrop = (type, e) => {
    e.preventDefault();
    setDragActive(prev => ({ ...prev, [type]: false }));
    const files = Array.from(e.dataTransfer.files);
    if (type === 'xte') {
      setXteFiles(files);
    } else {
      setXtrFiles(files);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="card-glass p-8 text-center">
        <div className="inline-block p-4 hero-gradient rounded-2xl mb-4">
          <svg className="w-16 h-16 accent-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h2 className="text-3xl font-bold gradient-text mb-3">
          Análise de Retorno ANS
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Cruze arquivos .XTR com .XTE e obtenha relatórios detalhados com as guias que apresentaram erro
        </p>
      </div>

      <div className="alert-warning">
        <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div>
          <p className="font-semibold">Atenção aos nomes dos arquivos</p>
          <p className="text-sm">Os arquivos XTE e XTR devem ter nomes correspondentes (ex: LOTE_123.xte e LOTE_123.xtr)</p>
        </div>
      </div>

      {/* ÁREA DE UPLOAD (SEMPRE VISÍVEL) */}
      <div className="grid lg:grid-cols-2 gap-6">
        <FileUploadCard
          label="Arquivos de Envio (.XTE)"
          icon="📤"
          accept=".xte"
          files={xteFiles}
          fileRef={xteFileRef}
          onChange={handleFileChange(setXteFiles)}
          onDrag={(active) => handleDrag('xte', active)}
          onDrop={(e) => handleDrop('xte', e)}
          dragActive={dragActive.xte}
          color="theme"
          fileType="xte"
        />
        <FileUploadCard
          label="Arquivos de Retorno (.XTR)"
          icon="📥"
          accept=".xtr"
          files={xtrFiles}
          fileRef={xtrFileRef}
          onChange={handleFileChange(setXtrFiles)}
          onDrag={(active) => handleDrag('xtr', active)}
          onDrop={(e) => handleDrop('xtr', e)}
          dragActive={dragActive.xtr}
          color="theme"
          fileType="xtr"
        />
      </div>

      {/* ÁREA DE AÇÃO (Troca dinâmica entre Analisar e Download) */}
      <div className="card p-8 transition-all duration-300 ease-in-out border-2 border-transparent shadow-lg">
        
        {!results ? (
          /* --- ESTADO 1: Botão de Analisar --- */
          <div className="text-center animate-fade-in">
            <ActionButton
              onClick={handleAnalyze}
              disabled={isAnyLoading || !fullAnalysisReady}
              loading={loading.analyzing}
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              }
              text="🔍 Analisar Retorno"
              loadingText="⏳ Analisando..."
              className="btn-primary btn-lg w-full sm:w-auto px-12 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
            />
            
            {anyFileSelected && (
              <div className="mt-4 text-center">
                <button
                  onClick={clearFiles}
                  className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-2 mx-auto group"
                >
                  <svg className="w-4 h-4 group-hover:rotate-180 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Limpar todos os arquivos
                </button>
              </div>
            )}
          </div>
        ) : (
          /* --- ESTADO 2: Área de Download (Substitui o botão acima) --- */
          <div className="text-center animate-scale-in">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center justify-center gap-2">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Análise Pronta para Download
            </h3>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <ActionButton
                onClick={handleDownloadAnalysis}
                disabled={isAnyLoading}
                loading={loading.downloadingAnalysis}
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
                text="📊 Baixar Excel (Análise Completa)"
                loadingText="📥 Baixando arquivo..."
                className="btn-success btn-lg w-full sm:w-auto px-8 py-3 shadow-md"
              />

              <button
                 onClick={clearFiles}
                 disabled={isAnyLoading}
                 className="px-6 py-3 rounded-lg font-medium text-gray-600 bg-gray-50 border border-gray-300 hover:bg-gray-100 transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Nova Análise
              </button>
            </div>
          </div>
        )}
      </div>

      {results && (
        <ResultsDisplay data={results} />
      )}
    </div>
  );
};

//Componente reutilizável para cards de upload
const FileUploadCard = ({ label, icon, accept, files, fileRef, onChange, onDrag, onDrop, dragActive, color, fileType }) => {
  const colorClasses = {
    blue: {
      border: 'border-gray-300',
      bg: 'bg-blue-50',
      activeBorder: 'border-blue-500',
      activeBg: 'bg-blue-100',
      icon: 'text-blue-600',
      badge: 'bg-blue-100 text-blue-700'
    },
    green: {
      border: 'border-gray-300',
      bg: 'bg-green-50',
      activeBorder: 'border-green-500',
      activeBg: 'bg-green-100',
      icon: 'text-green-600',
      badge: 'bg-green-100 text-green-700'
    },
    theme: {
      border: 'border-gray-300',
      bg: 'accent-bg',
      activeBorder: 'accent-border',
      activeBg: 'accent-bg',
      icon: 'accent-icon',
      badge: 'badge-theme'
    }
  };

  const colors = colorClasses[color] || colorClasses.theme;
  // Cria ID único baseado no tipo de arquivo para evitar conflitos
  const uniqueId = `file-${fileType || accept.replace('.', '')}`;

  return (
    <div className="card p-6">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-3xl">{icon}</span>
        <h3 className="text-lg font-semibold text-gray-800">{label}</h3>
      </div>
      
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
          dragActive ? `${colors.activeBorder} ${colors.activeBg} scale-105` : `${colors.border} hover:${colors.bg}`
        }`}
        onDragEnter={() => onDrag(true)}
        onDragLeave={() => onDrag(false)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
      >
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={accept}
          onChange={onChange}
          className="hidden"
          id={uniqueId}
        />
        <label htmlFor={uniqueId} className="cursor-pointer">
          <div className={`mx-auto w-16 h-16 ${colors.bg} rounded-full flex items-center justify-center mb-4`}>
            <svg className={`w-8 h-8 ${colors.icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          <p className="text-sm font-semibold text-gray-700 mb-1">
            Clique para selecionar ou arraste arquivos
          </p>
          <p className="text-xs text-gray-500">
            Formatos aceitos: {accept}
          </p>
        </label>
      </div>

      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <span className={colors.badge}>{files.length}</span>
            arquivo(s) selecionado(s)
          </p>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {files.map((file, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs bg-gray-50 rounded-lg p-2">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="truncate flex-1">{file.name}</span>
                <span className="text-gray-400">{(file.size / 1024).toFixed(1)} KB</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Componente ActionButton
const ActionButton = ({ onClick, disabled, loading, icon, text, loadingText, className }) => (
  <button
    onClick={onClick}
    disabled={disabled || loading}
    className={`${className} w-full`}
  >
    {loading ? (
      <>
        <div className="loading-spinner" />
        <span>{loadingText}</span>
      </>
    ) : (
      <>
        {icon}
        <span>{text}</span>
      </>
    )}
  </button>
);

//Componnte ValidationReport
const ValidationReport = ({ data }) => {
  if (!data || !data.summary) return null;

  const { summary } = data;

  return (
    <div className="card p-6 animate-scale-in">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center">
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-800">Resultados da Validação</h3>
          <p className="text-sm text-gray-600">Verificação de integridade concluída</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total de Arquivos"
          value={summary.total_files}
          icon="📁"
          color="blue"
        />
        <StatCard
          label="Arquivos XTE"
          value={summary.valid_xte_files}
          icon="📤"
          color="green"
        />
        <StatCard
          label="Arquivos XTR"
          value={summary.valid_xtr_files}
          icon="📥"
          color="purple"
        />
        <StatCard
          label="Status"
          value={summary.can_perform_full_analysis ? 'Pronto' : 'Incompleto'}
          icon={summary.can_perform_full_analysis ? '✅' : '⚠️'}
          color={summary.can_perform_full_analysis ? 'green' : 'amber'}
        />
      </div>
    </div>
  );
};

//Componente ResultsDisplay
const ResultsDisplay = ({ data }) => (
  <div className="card overflow-hidden animate-scale-in">
    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
      <div className="flex items-center gap-3 mb-2">
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <h3 className="text-2xl font-bold">Análise Completa</h3>
      </div>
      <p className="text-blue-100">Resultados detalhados do cruzamento de dados</p>
    </div>

    <div className="p-6">
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <StatCard
          label="Registros com Erro"
          value={data.data.total_records_with_errors}
          icon="⚠️"
          color="red"
        />
        <StatCard
          label="Total de Erros"
          value={data.data.total_errors_reported_in_xtr}
          icon="❌"
          color="orange"
        />
        <StatCard
          label="Tempo de Processamento"
          value={`${data.data.processing_time_seconds?.toFixed(2)}s`}
          icon="⏱️"
          color="blue"
        />
      </div>

      {data.data.records.length > 0 ? (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-800">Preview dos Dados (primeiras 10 linhas)</h4>
            <span className="badge-info">{data.data.records.length} registros totais</span>
          </div>
          <div className="table-container overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  {data.data.columns.map(col => (
                    <th key={col} className="table-header">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.data.records.slice(0, 10).map((record, idx) => (
                  <tr key={idx} className="table-row">
                    {data.data.columns.map(col => (
                      <td key={col} className="table-cell">
                        {record[col] || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-xl font-semibold text-gray-800 mb-2">Nenhum erro encontrado!</p>
          <p className="text-gray-600">Todos os registros foram processados com sucesso.</p>
        </div>
      )}
    </div>
  </div>
);

const StatCard = ({ label, value, icon, color }) => {
  const colorClasses = {
    blue: 'from-blue-50 to-blue-100 text-blue-600',
    green: 'from-green-50 to-emerald-100 text-green-600',
    purple: 'from-purple-50 to-indigo-100 text-purple-600',
    red: 'from-red-50 to-rose-100 text-red-600',
    orange: 'from-orange-50 to-amber-100 text-orange-600',
    amber: 'from-amber-50 to-yellow-100 text-amber-600'
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} rounded-xl p-4 card-interactive`}>
      <div className="flex items-start justify-between mb-2">
        <span className="text-2xl">{icon}</span>
      </div>
      <p className="text-3xl font-bold mb-1">{value}</p>
      <p className="text-sm font-medium opacity-80">{label}</p>
    </div>
  );
};

const XtrReportTab = ({ xtrFiles, setXtrFiles, loading, handleDownloadXtrReport, handleFileChange }) => {
  const xtrFileRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    setXtrFiles(files);
  };

  const clearFiles = () => {
    setXtrFiles([]);
    if (xtrFileRef.current) xtrFileRef.current.value = '';
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Hero Section */}
      <div className="card-glass p-8 text-center">
        <div className="inline-block p-4 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl mb-4">
          <svg className="w-16 h-16 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h2 className="text-3xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-3">
          Relatório XTR Formatado
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Converta arquivos .XTR em relatórios Excel formatados e organizados por tipo de erro
        </p>
      </div>

      <div className="alert-info">
        <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <p className="font-semibold">Sobre o Relatório XTR</p>
          <p className="text-sm">Este relatório processa apenas arquivos de retorno (.XTR) e gera um Excel formatado com todos os erros organizados e descritos conforme o padrão ANS.</p>
        </div>
      </div>

      <div className="card p-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          Upload de Arquivos XTR
        </h3>

        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
            dragActive 
              ? 'border-amber-500 bg-amber-50' 
              : 'border-gray-300 hover:border-amber-400 hover:bg-amber-50/30'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={xtrFileRef}
            type="file"
            accept=".xtr"
            multiple
            onChange={handleFileChange(setXtrFiles)}
            className="hidden"
            id="xtr-upload"
          />
          <label htmlFor="xtr-upload" className="cursor-pointer">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-700">
                  Clique para selecionar ou arraste arquivos .XTR
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Suporta múltiplos arquivos
                </p>
              </div>
            </div>
          </label>
        </div>

        {xtrFiles.length > 0 && (
          <div className="mt-6 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-700">
                📁 {xtrFiles.length} arquivo(s) selecionado(s)
              </p>
              <button
                onClick={clearFiles}
                className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Limpar
              </button>
            </div>
            <div className="grid gap-2 max-h-48 overflow-y-auto">
              {Array.from(xtrFiles).map((file, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {xtrFiles.length > 0 && (
        <div className="card p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Gerar Relatório
          </h3>
          <p className="text-gray-600 mb-4">
            Clique no botão abaixo para gerar o relatório Excel formatado com todos os erros identificados nos arquivos XTR.
          </p>
          <div className="text-center">
            <ActionButton
              onClick={() => handleDownloadXtrReport('standalone')}
              disabled={loading}
              loading={loading}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
              text="📊 Baixar Relatório XTR (Excel)"
              loadingText="⏳ Gerando Relatório..."
              className="btn-warning btn-lg px-8 py-3 w-full sm:w-auto"
            />
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <div className="card p-6 text-center">
          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h4 className="font-semibold text-gray-800 mb-2">Formatação Automática</h4>
          <p className="text-sm text-gray-600">Organiza erros por tipo e categoria conforme padrão ANS</p>
        </div>
        <div className="card p-6 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h4 className="font-semibold text-gray-800 mb-2">Processamento Rápido</h4>
          <p className="text-sm text-gray-600">Processa múltiplos arquivos XTR simultaneamente</p>
        </div>
        <div className="card p-6 text-center">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h4 className="font-semibold text-gray-800 mb-2">Descrições Completas</h4>
          <p className="text-sm text-gray-600">Cada erro vem com descrição detalhada da ANS</p>
        </div>
      </div>
    </div>
  );
};

export default TissAnalyzer;
